import React, { useEffect, useState } from "react";
import { api, fmtDuration } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Trash2, ArrowRight, Trophy, Train, Stamp as StampIcon, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Footer from "../components/Footer";

export default function Dashboard() {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, p] = await Promise.all([
        api.get("/routes"),
        api.get("/challenges"),
        api.get("/passport"),
      ]);
      setRoutes(r.data);
      setChallenges(c.data);
      setPassport(p.data);
    } catch (err) {
      // Session likely expired between page load and these calls.
      // Silently swallow — ProtectedRoute will redirect on the next render
      // tick once AuthContext re-reads /auth/session. No need to spam the
      // preview overlay with an AxiosError.
      if (err?.response?.status !== 401) {
        // eslint-disable-next-line no-console
        console.warn("Dashboard load failed:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const deleteRoute = async (id) => {
    if (!window.confirm("Delete this route?")) return;
    try {
      await api.delete(`/routes/${id}`);
      toast.success("Route removed.");
      load();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div data-testid="dashboard-page" className="pt-safe-navbar-lg pb-16">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="iq-overline mb-2">Conductor's desk</div>
            <h1 className="font-display text-cream text-5xl md:text-6xl tracking-wider">Welcome back, {user.name}</h1>
          </div>
          <Link to="/search" data-testid="dash-new-route" className="iq-btn-primary"><Plus className="h-4 w-4" /> New journey</Link>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Saved routes */}
          <section>
            <div className="iq-overline mb-3">Saved routes ({routes.length})</div>
            <div className="space-y-3">
              {loading && <div className="iq-card p-8 text-cream-dim font-serif italic">Loading routes…</div>}
              {!loading && routes.length === 0 && (
                <div className="iq-card p-12 text-center">
                  <Train className="h-10 w-10 text-gold/40 mx-auto mb-3" />
                  <p className="text-cream-dim font-serif italic mb-4">No saved routes yet. Start planning your grand tour.</p>
                  <Link to="/search" className="iq-btn-secondary">Plan a journey <ArrowRight className="h-4 w-4" /></Link>
                </div>
              )}
              {routes.map((r, i) => (
                <div key={r.id} data-testid={`route-card-${i}`} className="iq-card p-5 hover:border-gold/40 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-display text-2xl text-cream tracking-wider truncate">{r.name}</h3>
                      <p className="text-cream-dim text-xs mt-1">
                        {r.segments.length} {r.segments.length === 1 ? "leg" : "legs"} ·{" "}
                        {fmtDuration(r.total_duration_seconds)} · €{Math.round(r.total_price_eur)}
                      </p>
                    </div>
                    <button
                      data-testid={`route-delete-${i}`}
                      onClick={() => deleteRoute(r.id)}
                      className="text-cream-dim/60 hover:text-accent-red transition-colors p-1"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {r.segments.map((s, j) => (
                      <span key={`${r.id}-${j}-${s.from_city}-${s.to_city}`} className="text-[11px] px-2 py-1 rounded-sm bg-ink border border-panel-border text-cream-dim">
                        {s.from_city} <ArrowRight className="inline h-3 w-3 text-gold" /> {s.to_city}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="space-y-6">
            {/* XP Card */}
            <div className="iq-card p-6">
              <div className="iq-overline mb-3 flex items-center gap-2"><Trophy className="h-3.5 w-3.5" /> Standing</div>
              <div className="grid grid-cols-3 text-center gap-2">
                <div>
                  <div className="font-display text-3xl text-gold">LVL {user.level}</div>
                  <div className="text-[10px] text-cream-dim uppercase tracking-widest">Rank</div>
                </div>
                <div>
                  <div className="font-display text-3xl text-cream">{user.xp}</div>
                  <div className="text-[10px] text-cream-dim uppercase tracking-widest">XP</div>
                </div>
                <div>
                  <div className="font-display text-3xl text-cream">{passport?.stamps?.length || 0}</div>
                  <div className="text-[10px] text-cream-dim uppercase tracking-widest">Cities</div>
                </div>
              </div>
              <Link to="/passport" className="iq-btn-secondary w-full mt-4 text-xs py-2">
                <StampIcon className="h-4 w-4" /> View passport
              </Link>
            </div>

            {/* Challenges */}
            <div className="iq-card p-6">
              <div className="iq-overline mb-3">Challenges</div>
              <div className="space-y-3">
                {challenges.map((c) => (
                  <div key={c.id} data-testid={`challenge-${c.id}`} className="border-l-2 border-gold/60 pl-3">
                    <div className="flex items-center justify-between">
                      <span className="font-display text-cream tracking-wider">{c.name}</span>
                      <span className="text-[10px] text-gold">+{c.xp} XP</span>
                    </div>
                    <p className="text-xs text-cream-dim leading-relaxed mt-0.5">{c.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
